import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerFeature } from '../hooks/usePartnerFeature';
import { useAuth } from '../context/AuthContext';

interface RequirePartnerFeatureProps {
  feature: string;
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Route guard that blocks access to a page when the named partner-level
 * feature flag is disabled. Used to gate partner-scoped modules (e.g.
 * recurring tasks / approvals) so a partner who doesn't have the feature
 * cannot reach the page via deep link.
 *
 * Waits for the auth context to finish loading so we don't bounce users
 * before partnerFeatures has been hydrated.
 */
export function RequirePartnerFeature({ feature, children, redirectTo = '/' }: RequirePartnerFeatureProps) {
  const { isLoading, partnerAuthContext } = useAuth();
  const enabled = usePartnerFeature(feature);

  if (isLoading || !partnerAuthContext) return null;
  if (!enabled) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}
